import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  Link2,
  Check,
  Users,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useIsMobile } from '../../hooks/use-mobile';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  API_CLIENTS,
  API_CLIENTS_NAME,
  API_KEY,
  API_NEWCLIENT,
  GET_ZOHO_ACCOUNTS,
  UPDATE_ZOHO_ACCOUNT
} from '../../utils/constants';
import { setSelectedClient } from '../../store/slices/selectedClient';
import NewGoogleConnection from '../../components/NewGoogleConnection';
import { buildClientPayload } from '../Onboarding/clientPayload';
import { accountManagers } from '../../utils';

const axios = require('axios');
const PAGE_SIZE = 50;

const isAssigned = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value !== 'string') return false;

  return !['', '0', 'false', 'null', 'unassigned'].includes(
    value.trim().toLowerCase()
  );
};

const getZohoId = (client) =>
  client?.clientSocialNetworks?.zohoId ||
  client?.socialnetworks?.zohoId ||
  client?.clientSocialNetworks?.zohoID ||
  client?.socialnetworks?.zohoID;

const getClientAddress = (client) =>
  [
    client.Billing_Street,
    client.Billing_City,
    client.Billing_State,
    client.Billing_Code,
    client.Billing_Country
  ]
    .filter(Boolean)
    .join(', ');

const getAccountManager = (client) => {
  const owner =
    client['Account Owner'] ||
    client.Account_Owner ||
    client.Owner ||
    client.AccountOwner ||
    client.Owner_Name;
  const ownerName =
    typeof owner === 'object' && owner !== null
      ? owner.name ||
        owner.full_name ||
        owner.fullName ||
        owner.Name ||
        [owner.first_name, owner.last_name].filter(Boolean).join(' ')
      : owner;

  if (ownerName && !/^\d+$/.test(String(ownerName))) {
    return String(ownerName);
  }

  const ownerId =
    (typeof owner === 'object' && owner !== null
      ? owner.id || owner.ID || owner.value
      : ownerName) ||
    client.Account_Owner_ID ||
    client.Owner_ID;
  const accountManager = accountManagers.find(
    (manager) => String(manager.id) === String(ownerId)
  );

  return accountManager?.full_name || (ownerId ? String(ownerId) : '—');
};

export default function ConnectClient() {
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  const accessToken = useSelector((state) => state.auth.userInfo?.accessToken);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [crmClients, setCrmClients] = useState([]);
  const [suiteClients, setSuiteClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClientId, setLoadingClientId] = useState(null);
  const [error, setError] = useState('');
  const [googleConnectionOpen, setGoogleConnectionOpen] = useState(false);
  const [pendingOnboarding, setPendingOnboarding] = useState(null);
  const pendingOnboardingRef = useRef(null);

  useEffect(() => {
    document.title = 'Connect Client, Adler Local';
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim().toLowerCase());
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filter]);

  useEffect(() => {
    let mounted = true;

    const loadClients = async () => {
      setLoading(true);
      setError('');

      try {
        const [zohoResponse, suiteResponse] = await Promise.all([
          axios.get(
            `${GET_ZOHO_ACCOUNTS}${
              GET_ZOHO_ACCOUNTS.includes('?') ? '&' : '?'
            }Account_Status=Active`
          ),
          axios.get(API_CLIENTS, {
            headers: { 'X-API-KEY': API_KEY }
          })
        ]);
        const accounts = zohoResponse.data?.body;
        const clients = suiteResponse.data?.clients;

        if (!Array.isArray(accounts) || !Array.isArray(clients)) {
          throw new Error('Zoho or suite returned an unexpected client list.');
        }

        if (mounted) {
          setCrmClients(accounts);
          setSuiteClients(clients);
        }
      } catch (loadError) {
        console.error('Error loading Zoho CRM clients:', loadError);
        if (mounted) {
          setError(
            'Could not load Zoho CRM clients. Please try again in a moment.'
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadClients();
    return () => {
      mounted = false;
    };
  }, []);

  const suiteClientsByZohoId = useMemo(() => {
    const clientMap = new Map();
    suiteClients.forEach((client) => {
      const zohoId = getZohoId(client);
      if (zohoId) clientMap.set(String(zohoId), client);
    });
    return clientMap;
  }, [suiteClients]);

  const clients = useMemo(
    () =>
      crmClients
        .map((client) => {
          const id = String(client.id || client.ID || '');
          const suiteClient = suiteClientsByZohoId.get(id);
          const connected =
            Boolean(suiteClient) || isAssigned(client.assignedClient);

          return {
            ...client,
            id,
            name: client.Account_Name || client.name || 'Unnamed Zoho account',
            manager: getAccountManager(client),
            connected
          };
        })
        .sort(
          (first, second) => Number(first.connected) - Number(second.connected)
        ),
    [crmClients, suiteClientsByZohoId]
  );

  const activeCount = useMemo(
    () =>
      clients.reduce((count, client) => count + Number(client.connected), 0),
    [clients]
  );
  const filtered = useMemo(
    () =>
      clients.filter((client) => {
        const matchesSearch =
          !debouncedSearch ||
          `${client.name} ${client.manager} ${client.id}`
            .toLowerCase()
            .includes(debouncedSearch);
        return (
          matchesSearch &&
          (filter === 'all' ||
            (filter === 'active' ? client.connected : !client.connected))
        );
      }),
    [clients, debouncedSearch, filter]
  );
  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const pageClients = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const firstVisible = filtered.length === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastVisible = Math.min((page + 1) * PAGE_SIZE, filtered.length);

  useEffect(() => {
    if (pageCount > 0 && page >= pageCount) {
      setPage(pageCount - 1);
    }
  }, [page, pageCount]);

  const onboardClient = async (client) => {
    setLoadingClientId(client.id);
    setError('');

    try {
      const accountName = String(client.name || '').trim();
      if (!accountName || !client.id) {
        throw new Error('Zoho account is missing its name or CRM ID.');
      }
      if (!API_NEWCLIENT || !API_CLIENTS_NAME || !UPDATE_ZOHO_ACCOUNT) {
        throw new Error('Client onboarding services are not configured.');
      }

      const website = client.Website || '';
      const suitePayload = {
        ...buildClientPayload({
          name: accountName,
          email: client.Email || client.Email_Address || '',
          phone: client.Phone || '',
          website: { url: website },
          address: { street: getClientAddress(client) }
        }),
        socialnetworks: {
          zoho: true,
          zohoId: client.id
        }
      };

      await axios.post(API_NEWCLIENT, suitePayload);

      const clientResponse = await axios.get(
        `${API_CLIENTS_NAME}${encodeURIComponent(accountName)}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            ...(accessToken ? { Authorization: `bearer ${accessToken}` } : {})
          }
        }
      );
      const suiteClient = clientResponse.data;
      if (!suiteClient?.id) {
        throw new Error(
          'The suite created the client but did not return its ID.'
        );
      }

      await axios.post(UPDATE_ZOHO_ACCOUNT, {
        id: client.id,
        Account_Name: accountName,
        currentClientId: suiteClient.id
      });

      const selectedClient = {
        ...suiteClient,
        clientSocialNetworks: {
          ...(suiteClient.clientSocialNetworks ||
            suiteClient.socialnetworks ||
            {}),
          zoho: true,
          zohoId: client.id
        },
        socialnetworks: {
          ...(suiteClient.socialnetworks || {}),
          zoho: true,
          zohoId: client.id
        }
      };
      dispatch(setSelectedClient(selectedClient));
      const onboardingTarget = {
        selectedClient,
        zohoClientId: client.id,
        clientName: accountName
      };
      pendingOnboardingRef.current = onboardingTarget;
      setPendingOnboarding(onboardingTarget);
      setGoogleConnectionOpen(true);
    } catch (onboardError) {
      console.error('Error onboarding Zoho CRM client:', onboardError);
      setError(
        onboardError.response?.data?.message ||
          onboardError.message ||
          'Could not onboard this client. Please try again.'
      );
    } finally {
      setLoadingClientId(null);
    }
  };

  const handleGoogleLocationConnected = (location) => {
    const target = pendingOnboardingRef.current;
    if (!target || !location) return;

    // Google state is not written to Redux; Onboarding confirms it with the backend.
    const updatedTarget = {
      ...target,
      googleConnectionPending: true,
      pendingGoogleLocation: location
    };
    pendingOnboardingRef.current = updatedTarget;
    setPendingOnboarding(updatedTarget);
  };

  const continueToOnboarding = (open) => {
    setGoogleConnectionOpen(open);
    if (open || !pendingOnboardingRef.current) return;

    const target = pendingOnboardingRef.current;
    pendingOnboardingRef.current = null;
    setPendingOnboarding(null);
    dispatch(setSelectedClient(target.selectedClient));
    history.push('/onboarding', {
      zohoClientId: target.zohoClientId,
      clientName: target.clientName,
      verifyGoogleConnection: Boolean(target.googleConnectionPending),
      pendingGoogleLocation: target.pendingGoogleLocation
    });
  };

  const status = (client) => (
    <Badge
      variant="outline"
      className={
        client.connected
          ? 'gap-1 text-primary border-primary/30 bg-primary/5'
          : 'text-muted-foreground'
      }>
      {client.connected && <Check className="h-3 w-3" />}
      {client.connected ? 'Connected' : 'Not connected'}
    </Badge>
  );

  const action = (client) =>
    client.connected ? (
      <span className="text-sm text-muted-foreground">—</span>
    ) : (
      <Button
        className="h-14 w-full sm:w-auto"
        disabled={loadingClientId !== null || pendingOnboarding !== null}
        onClick={() => onboardClient(client)}
        aria-label={`Onboard ${client.name}`}>
        {loadingClientId === client.id ? (
          <Loader2 className="animate-spin" />
        ) : (
          <Link2 />
        )}
        {loadingClientId === client.id ? 'Onboarding...' : 'Onboard Client'}
      </Button>
    );

  return (
    <main className="min-w-0 overflow-x-hidden">
      <div className="space-y-6 p-4 sm:p-6 lg:p-8">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground">
              Connect Client
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              CRM clients <span className="mx-2">·</span> {activeCount}{' '}
              connected / {clients.length} total
            </p>
          </div>
          <Badge variant="secondary" className="w-fit gap-2">
            <Users className="h-4 w-4" />
            Zoho CRM data
          </Badge>
        </header>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search CRM clients"
              placeholder="Search CRM clients"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-14 pl-12"
            />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger
              aria-label="Connection status"
              className="h-14 w-full sm:w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              <SelectItem value="available">Not connected</SelectItem>
              <SelectItem value="active">Connected</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading Zoho CRM clients...
          </div>
        ) : (
          <>
            {isMobile ? (
              <div className="space-y-3">
                {pageClients.map((client) => (
                  <article
                    key={client.id}
                    className="space-y-4 rounded-lg border border-border bg-card p-4">
                    <div>
                      <h2 className="font-semibold break-words text-foreground">
                        {client.name}
                      </h2>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {client.id}
                      </p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {client.manager}
                    </p>
                    {status(client)}
                    {action(client)}
                  </article>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-border">
                <Table className="[&_th]:border-r-0 [&_td]:border-r-0">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Client Name</TableHead>
                      <TableHead>CRM ID</TableHead>
                      <TableHead>Account Manager</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pageClients.map((client) => (
                      <TableRow key={client.id}>
                        <TableCell className="font-medium text-foreground">
                          {client.name}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {client.id}
                        </TableCell>
                        <TableCell>{client.manager}</TableCell>
                        <TableCell>{status(client)}</TableCell>
                        <TableCell className="text-right">
                          {action(client)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {filtered.length === 0 && (
              <p className="py-12 text-center text-muted-foreground">
                No clients found.
              </p>
            )}
            {filtered.length > 0 && (
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  Showing {firstVisible}–{lastVisible} of {filtered.length}{' '}
                  clients
                </p>
                <div className="flex items-center gap-2 sm:mr-16">
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Previous page"
                    disabled={page === 0}
                    onClick={() => setPage((current) => current - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="min-w-24 text-center text-sm text-muted-foreground">
                    Page {page + 1} of {pageCount}
                  </span>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Next page"
                    disabled={page + 1 >= pageCount}
                    onClick={() => setPage((current) => current + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <NewGoogleConnection
        open={googleConnectionOpen}
        onOpenChange={continueToOnboarding}
        currentClientId={pendingOnboarding?.selectedClient?.id}
        currentClient={pendingOnboarding?.selectedClient}
        connectSnCallback={handleGoogleLocationConnected}
        skipLabel="Continue without Google"
      />
    </main>
  );
}
